/**
 * Výpočty kalkulačky v ASO/SO s více investičními strategiemi.
 *
 * Data kroku (step.data):
 *  - strategies: [{strategy_id, name, initial_deposit, regular_deposit, duration, alloc_pct}] – karty strategií,
 *  - mode: 'SAVING' (investiční plán – částky zadává uživatel u každé strategie)
 *          nebo 'TARGET' (investiční cíl – pravidelná investice se dopočítá z cílové částky a rozdělení),
 *  - parameters.target.amount a parameters.general.duration – cílová částka a horizont režimu cíle,
 *  - goal_split: 'EVEN' / 'CUSTOM' – rozdělení cílové částky mezi strategie,
 *  - parameters.saving.initial_deposit / regular_deposit – společné částky pro všechny strategie (zaškrtávátka v režimu plánu),
 *  - strategy_id – první strategie, kvůli místům na BE, která čtou jen jednu strategii.
 *
 * Výpočet vývoje hodnoty je stejný jako u dřívější kalkulačky s jednou strategií: vklad na začátku měsíce,
 * měsíční úrok = roční výkonnost strategie / 12. U dopočtu pravidelné investice z cíle zůstává dosavadní vzorec
 * (efektivní měsíční úrok z roční výkonnosti).
 */

/** Roční míra pro srovnávací linku „Předpokládaná míra inflace“ (beze změny oproti dřívější kalkulačce). */
export const INFLATION_RATE = 0.03;

/** Barvy linek a teček strategií v grafu i na kartách (podle draftu kalkulačky). */
export const STRATEGY_COLORS = ['#6c63a6', '#3e7cb1', '#b0356b', '#004148', '#c9a227', '#5a9e6f'];

/** Barvy pomocných linek grafu – stejné jako v dřívější kalkulačce. */
export const DEPOSITS_COLOR = 'rgba(199,122,86,1)';
export const INFLATION_COLOR = 'rgba(4,110,122,1)';

/** Výkonnost strategie jako číslo (8 % = 0.08); BE ji může vrátit i jako řetězec. */
export function strategyRate(strategy) {
  const rate = typeof strategy?.performance === 'number' ? strategy.performance : parseFloat(strategy?.performance);
  return Number.isFinite(rate) ? rate : 0;
}

/**
 * Pravidelná měsíční investice potřebná k dosažení cílové částky (beze změny oproti dřívější kalkulačce).
 */
export function monthlyInvestmentForTarget(finalAmount, annualRate, years) {
  if (!Number.isFinite(finalAmount) || !Number.isFinite(annualRate) || !Number.isFinite(years) || years <= 0) {
    return 0;
  }
  const months = years * 12;
  const monthlyRate = Math.pow(1 + annualRate, 1 / 12) - 1;
  if (Math.abs(monthlyRate) < 1e-9) {
    return finalAmount / months;
  }
  return (finalAmount * monthlyRate) / (Math.pow(1 + monthlyRate, months) - 1);
}

/**
 * Vývoj vkladů a hodnoty po letech (1..years). Vklad na začátku měsíce, úrok rate / 12.
 * @returns {{deposits: number[], values: number[]}}
 */
export function yearlySeries(initialDeposit, regularDeposit, years, rate) {
  const deposits = [];
  const values = [];
  let depositsTotal = 0;
  let value = 0;
  for (let year = 1; year <= years; year++) {
    for (let month = 1; month <= 12; month++) {
      const deposit = (year === 1 && month === 1 ? initialDeposit : 0) + regularDeposit;
      depositsTotal += deposit;
      value = (value + deposit) * (1 + rate / 12);
    }
    deposits.push(depositsTotal);
    values.push(value);
  }
  return {deposits, values};
}

/** Procento cílové částky pro strategii: rovnoměrně, nebo vlastní rozdělení. */
export function allocationPct(data, item) {
  const count = data.strategies?.length || 1;
  if (data.goal_split === 'CUSTOM') {
    return Number(item.alloc_pct) || 0;
  }
  return 100 / count;
}

/**
 * V režimu cíle dopočítá u každé strategie pravidelnou investici z cílové částky a rozdělení
 * (jednorázová je 0, horizont společný). Hodnoty zapisuje přímo do položek – posílají se na BE.
 */
export function applyTargetMode(data, strategyById) {
  if (data.mode !== 'TARGET') {
    return;
  }
  const amount = Number(data.parameters?.target?.amount) || 0;
  const years = Number(data.parameters?.general?.duration) || 1;
  for (const item of data.strategies ?? []) {
    const pct = allocationPct(data, item);
    item.initial_deposit = 0;
    item.duration = years;
    item.regular_deposit = Math.round(monthlyInvestmentForTarget(amount * pct / 100, strategyRate(strategyById(item.strategy_id)), years));
    if (data.goal_split !== 'CUSTOM') {
      item.alloc_pct = Math.round(pct * 100) / 100;
    }
  }
}

/**
 * Výsledek jedné karty: vloženo, očekávaná hodnota a zhodnocení na konci jejího horizontu.
 */
export function strategyResult(item, strategy) {
  const years = Math.max(1, Number(item.duration) || 1);
  const {deposits, values} = yearlySeries(Number(item.initial_deposit) || 0, Number(item.regular_deposit) || 0, years, strategyRate(strategy));
  const invested = deposits[deposits.length - 1] ?? 0;
  const expected = values[values.length - 1] ?? 0;
  return {invested, expected, gain: expected - invested, rate: strategyRate(strategy)};
}

/**
 * Data grafu: Vložené prostředky (součet), linka za každou strategii a Předpokládaná míra inflace (součet).
 * Po konci horizontu strategie její hodnota i vklady zůstávají na konečné hodnotě, aby šly strategie
 * s různým horizontem sečíst.
 */
export function chartData(data, strategyById, labels) {
  const items = data.strategies ?? [];
  const maxYears = Math.max(1, ...items.map(item => Number(item.duration) || 1));
  const categories = Array.from({length: maxYears}, (_, i) => i + 1);
  const atYear = (series, year) => series[Math.min(year, series.length) - 1] ?? 0;

  const perItem = items.map(item => {
    const years = Math.max(1, Number(item.duration) || 1);
    const initial = Number(item.initial_deposit) || 0;
    const regular = Number(item.regular_deposit) || 0;
    return {
      item,
      strategy: yearlySeries(initial, regular, years, strategyRate(strategyById(item.strategy_id))),
      inflation: yearlySeries(initial, regular, years, INFLATION_RATE),
    };
  });

  const series = [{
    name: labels.deposits,
    data: categories.map(year => perItem.reduce((sum, p) => sum + atYear(p.strategy.deposits, year), 0)),
    itemStyle: {color: DEPOSITS_COLOR},
  }];
  perItem.forEach((p, index) => series.push({
    name: p.item.name || strategyById(p.item.strategy_id)?.strategy_name || '',
    data: categories.map(year => atYear(p.strategy.values, year)),
    itemStyle: {color: STRATEGY_COLORS[index % STRATEGY_COLORS.length]},
  }));
  series.push({
    name: labels.inflation,
    data: categories.map(year => perItem.reduce((sum, p) => sum + atYear(p.inflation.values, year), 0)),
    itemStyle: {color: INFLATION_COLOR},
  });

  return {categories, series};
}

/**
 * Převede data kroku na seznam strategií: starý tvar (jedna strategie ve strategy_id) na jednu kartu,
 * kalkulačku otevřenou poprvé na kartu strategie s nejvyšší výkonností (dosavadní výchozí volba).
 * Uložený prázdný seznam zůstává prázdný – onboarding může obsahovat jen dluhopisy.
 */
export function normalizeStrategies(data, availableStrategies) {
  // seznam strategií ještě nikdy nebyl uložený (nová kalkulačka nebo starý tvar s jednou strategií)
  const isFirstOpen = !Array.isArray(data.strategies);
  if (isFirstOpen) {
    data.strategies = [];
  }
  const available = Array.isArray(availableStrategies) ? availableStrategies : [];
  // strategie, které mezitím z nabídky dotazníku vypadly, z karet zmizí
  data.strategies = data.strategies.filter(item => available.some(s => s.id === Number(item.strategy_id)));

  if (isFirstOpen && !data.strategies.length && available.length) {
    const legacy = available.find(s => s.id === Number(data.strategy_id));
    const best = legacy ?? available.reduce((a, b) => (strategyRate(b) > strategyRate(a) ? b : a), available[0]);
    const isTarget = data.mode === 'TARGET';
    data.strategies.push({
      strategy_id: best.id,
      name: best.strategy_name ?? best.name,
      initial_deposit: isTarget ? 0 : Number(data.parameters?.saving?.initial_deposit) || 0,
      regular_deposit: isTarget ? Number(data.monthly_deposit) || 0 : Number(data.parameters?.saving?.regular_deposit) || 0,
      duration: Number(data.parameters?.general?.duration) || 30,
      alloc_pct: 100,
    });
  }

  for (const item of data.strategies) {
    item.strategy_id = Number(item.strategy_id);
    item.initial_deposit = Number(item.initial_deposit) || 0;
    item.regular_deposit = Number(item.regular_deposit) || 0;
    item.duration = Number(item.duration) || 1;
  }
  if (!data.goal_split) {
    data.goal_split = 'EVEN';
  }
}

/**
 * Údaje pro místa na BE, která čtou jen jednu strategii (strategy_id, název, výkonnost).
 */
export function syncLegacyFields(data, strategyById) {
  const first = data.strategies?.[0];
  const strategy = first ? strategyById(first.strategy_id) : null;
  data.strategy_id = first ? first.strategy_id : null;
  data.strategy_name = strategy?.strategy_name ?? strategy?.name ?? null;
  data.performance = strategy ? strategyRate(strategy) : null;
}

/**
 * Generovaná textace pro záznam z jednání: úvod (ASO / SO), každá strategie s horizontem a vklady,
 * na konci strategie Dluhopisy s nakupovanými dluhopisy.
 *
 * @param {object} data data kroku Kalkulačka
 * @param {function} strategyById vyhledání strategie z nabídky dotazníku
 * @param {Array} bondsOffer nabídka dluhopisů z kroku (bonds.available)
 * @param {object} helpers {t, formatAmount, formatYearsString, isAssistedOnboarding}
 */
export function summaryText(data, strategyById, bondsOffer, helpers) {
  const {t, formatAmount, formatYearsString, isAssistedOnboarding} = helpers;
  const currency = data.parameters?.general?.currency ?? 'CZK';
  const lines = [];
  lines.push((isAssistedOnboarding ? t('clientOnboardingOverlay.calculation_summary_text_1_aso') : t('clientOnboardingOverlay.calculation_summary_text_1'))
    + (data.mode === 'TARGET' ? t('onboardingCalculation.summary_goal') : t('onboardingCalculation.summary_plan'))
    + ' ' + t('onboardingCalculation.summary_composed'));

  const items = data.strategies ?? [];
  items.forEach((item, index) => {
    const name = item.name || strategyById(item.strategy_id)?.strategy_name || '';
    lines.push('');
    lines.push((index + 1) + '. ' + t('onboardingCalculation.summary_strategy', {name}));
    lines.push(t('onboardingCalculation.summary_horizon', {years: formatYearsString(item.duration)}));
    lines.push(t('onboardingCalculation.summary_initial', {amount: formatAmount(item.initial_deposit, currency, 0)}));
    lines.push(t('onboardingCalculation.summary_regular', {amount: formatAmount(item.regular_deposit, currency, 0)}));
  });

  const bonds = (data.bonds ?? []).map(selected => {
    const bond = (bondsOffer ?? []).find(b => b.id === Number(selected.bond_id));
    return bond ? t('onboardingCalculation.summary_bond', {
      name: bond.name,
      isin: bond.isin ?? '',
      count: selected.quantity,
      amount: formatAmount((Number(bond.nominal_value) || 0) * (Number(selected.quantity) || 0), bond.currency ?? 'CZK', 0),
    }) : null;
  }).filter(Boolean);
  if (bonds.length) {
    lines.push('');
    lines.push((items.length + 1) + '. ' + t('onboardingCalculation.summary_bonds'));
    lines.push(...bonds);
  }

  return lines.join('\n');
}
